import _ from "underscore";
import UNINITIALISED from "utils/uninitialised";
import UserActionTypes from "modules/user/constants/action_types";
import ActionTypes from "../constants/action_types";

function handleDocumentUpdate(state, payload) {
  if (state.id !== payload.id || payload.last_edited <= state.last_edited) {
    return state;
  }
  if (
    payload.meta_data_feedback !== undefined &&
    Object.keys(payload.meta_data_feedback).length !== 0
  ) {
    const {name} = payload.meta_data_feedback;
    return {
      ...state,
      last_edited: payload.last_edited,
      meta_data_feedback: {
        ...state.meta_data_feedback,
        [name]: {
          ...(state.meta_data_feedback[name]
            ? state.meta_data_feedback[name]
            : {}),
          ...payload.meta_data_feedback.updates,
        },
      },
    };
  }
  if (
    payload.output_text !== undefined &&
    Object.keys(payload.output_text).length !== 0
  ) {
    return {
      ...state,
      last_edited: payload.last_edited,
      output_text: {
        ...state.output_text,
        ...payload.output_text,
      },
    };
  }
  return {
    ...state,
    ...payload,
    partyUpdateCount: _.isEqual(payload.parties, state.parties)
      ? state.partyUpdateCount
      : state.partyUpdateCount + 1,
  };
}

export default function documentReducer(state = UNINITIALISED, action) {
  switch (action.type) {
    case ActionTypes.DOCUMENT_FETCH.SUCCESS:
    case ActionTypes.MATTER_DOCUMENT_FETCH.SUCCESS:
      return state === UNINITIALISED
        ? {...action.payload, partyUpdateCount: 0}
        : {...state, ...(action.payload || {}), partyUpdateCount: 0};
    case ActionTypes.DOCUMENT_UPDATE.SUCCESS:
      return handleDocumentUpdate(state, action.payload);
    case ActionTypes.DOCUMENT_ISSUE_MANUAL_CORRECT.SUCCESS: {
      if (state === UNINITIALISED) {
        return state;
      }
      const {payload} = action;
      if (
        payload &&
        payload.document_id &&
        state.id === payload.document_id &&
        payload.document_last_edited
      ) {
        return {...state, last_edited: payload.document_last_edited};
      }
      return state;
    }
    case ActionTypes.ISSUESET_REMOVE.SUCCESS: {
      if (state === UNINITIALISED) {
        return state;
      }
      return {
        ...state,
        issuesets: state.issuesets.filter(id => id !== action.payload.id),
      };
    }
    case ActionTypes.DOCUMENT_DATA_FETCH.SUCCESS:
      return {...state, documentData: action.payload};
    case ActionTypes.DOCUMENT_CLEAR.SUCCESS:
      return UNINITIALISED;
    case ActionTypes.DOCUMENT_CLAUSE_ADD.SUCCESS:
    case ActionTypes.DOCUMENT_CLAUSEPART_ADD.SUCCESS:
    case ActionTypes.DOCUMENT_ISSUE_CORRECT.SUCCESS:
      return {...state, last_edited: action.payload.document_last_edited};
    case ActionTypes.DOCUMENT_STATE_REVERT.SUCCESS: {
      if (state.id === action.payload.id) {
        return {
          ...state,
          clauses: action.payload.clauses,
          last_edited: action.payload.document_last_edited,
          ...(action.payload.document_updates
            ? action.payload.document_updates
            : {}),
        };
      }
      return state;
    }
    case UserActionTypes.USER_LOGOUT.SUCCESS:
      return UNINITIALISED;
    case ActionTypes.CLAUSEPART_COMMENT_ADD.SUCCESS: {
      if (state === UNINITIALISED) {
        return state;
      }
      return {
        ...state,
        comments: [...(state.comments || []), action.payload],
      };
    }
    case ActionTypes.CLAUSEPART_COMMENT_UPDATE.SUCCESS: {
      if (state === UNINITIALISED) {
        return state;
      }
      const updatedComment = action.payload;
      return {
        ...state,
        comments: (state.comments || []).map(comment => {
          if (comment.id === updatedComment.id) {
            return updatedComment;
          }
          return comment;
        }),
      };
    }
    case ActionTypes.CLAUSEPART_COMMENT_DELETE.SUCCESS: {
      if (state === UNINITIALISED) {
        return state;
      }
      return {
        ...state,
        comments: (state.comments || []).filter(
          ({id}) => id !== action.payload.id,
        ),
      };
    }
    case ActionTypes.DOCUMENT_FILE_FETCH.SUCCESS: {
      if (state === UNINITIALISED) {
        return action.payload;
      }
      return {
        ...state,
        data: action.payload.data,
      };
    }
    case ActionTypes.DOCUMENT_ROLES_REPROCESS.SUCCESS: {
      if (state === UNINITIALISED || !action.payload) {
        return state;
      }
      return {
        ...state,
        parties: action.payload.parties,
        partyUpdateCount: state.partyUpdateCount + 1,
      };
    }
    case ActionTypes.DOCUMENT_ISSUES_REFRESH.SUCCESS: {
      if (
        state === UNINITIALISED ||
        !action.payload ||
        action.payload.document_id !== state.id
      ) {
        return state;
      }
      return {
        ...state,
        issues_last_processed: action.payload.issues_last_processed,
      };
    }
    case ActionTypes.DOCUMENT_ISSUE_UPDATE.SUCCESS: {
      if (state === UNINITIALISED) {
        return state;
      }
      return {
        ...state,
        ..._.omit(action.payload, "document_id"),
      };
    }
    case ActionTypes.CONVERSATIONS_FETCH.SUCCESS:
    case ActionTypes.DC_CONVERSATIONS_FETCH.SUCCESS: {
      return {
        ...state,
        conversations: action.payload.conversations,
      };
    }
    case ActionTypes.DOCUMENT_CONVERSATION_UPDATE_PRIVACY.SUCCESS: {
      if (state === UNINITIALISED) {
        return state;
      }

      const {id, is_private} = action.payload;

      const updatedConversations = Object.keys(state.conversations).reduce(
        (acc, conversationType) => {
          const conversations = state.conversations[conversationType];
          if (conversations[id]) {
            acc[conversationType] = {
              ...conversations,
              [id]: {
                ...conversations[id],
                is_private,
              },
            };
          } else {
            acc[conversationType] = conversations;
          }
          return acc;
        },
        {},
      );

      return {
        ...state,
        conversations: updatedConversations,
      };
    }
    case ActionTypes.DC_DOCUMENT_CONVERSATION_ITEMS_FETCH.SUCCESS:
    case ActionTypes.DOCUMENT_CONVERSATION_ITEMS_FETCH.SUCCESS:
    case ActionTypes.GENERATE_DC_PROMPT.SUCCESS:
    case ActionTypes.GENERATE_PROMPT.SUCCESS: {
      if (state === UNINITIALISED) {
        return state;
      }
      const {payload} = action;
      if (
        state.document_id !== payload.document_id &&
        state.document_id !== payload.dcDocumentId &&
        state.dc_document_id !== payload.document_id
      ) {
        return state;
      }
      const isGenerated = action.type === ActionTypes.GENERATE_PROMPT.SUCCESS;
      const existingConversation =
        state.conversations?.[payload.conversation_type]?.[
          payload.conversation.id
        ];
      const existingItems = existingConversation?.items || [];
      const mergedItems = payload.conversation.items.map(newItem => {
        const existingItem = existingItems.find(item => item.id === newItem.id);
        if (existingItem && existingItem.amendments && !newItem.amendments) {
          return {...newItem, amendments: existingItem.amendments};
        }
        return newItem;
      });
      return {
        ...state,
        conversations: {
          ...state.conversations,
          [payload.conversation_type]: {
            ...state.conversations?.[payload.conversation_type],
            [payload.conversation.id]: {
              ...existingConversation,
              is_private: payload.conversation.is_private,
              id: payload.conversation.id,
              ...(isGenerated ? {isNew: true} : {}),
              ...(payload.conversation.first_item_id
                ? {first_item_id: payload.conversation.first_item_id}
                : {}),
              ...(payload.conversation.first_user
                ? {first_user: payload.conversation.first_user}
                : {}),
              items: mergedItems,
            },
          },
        },
      };
    }
    case ActionTypes.CONVERSATION_ITEM_FEEDBACK_UPDATE.SUCCESS: {
      if (state === UNINITIALISED) {
        return state;
      }
      const {payload} = action;
      if (state.document_id !== payload.document_id) {
        return state;
      }
      return {
        ...state,
        conversations: {
          ...state.conversations,
          // Search through conversation items and update only the item that the feedback was successful for
          ...Object.keys(state.conversations).reduce(
            (conversations, conversationType) => {
              const conversation = state.conversations[conversationType];
              conversations[conversationType] = {
                ...conversation,
                ...Object.keys(conversation).reduce(
                  (conversationTypeConversations, conversationId) => {
                    const currentConversation = conversation[conversationId];
                    conversationTypeConversations[conversationId] = {
                      ...currentConversation,
                      ...(currentConversation.items
                        ? {
                            items: currentConversation.items.map(item =>
                              item.id === payload.id
                                ? {...item, feedback_success: true}
                                : item,
                            ),
                          }
                        : {}),
                    };
                    return conversationTypeConversations;
                  },
                  {},
                ),
              };

              return conversations;
            },
            {},
          ),
        },
      };
    }
    case ActionTypes.DOCUMENT_AMENDMENTS_FETCH.SUCCESS:
      return state === UNINITIALISED
        ? state
        : {...state, amendments: action.payload ?? []};
    case ActionTypes.DOCUMENT_ISSUE_AMENDMENTS_FETCH.SUCCESS:
      return state === UNINITIALISED
        ? state
        : {...state, issueAmendments: action.payload ?? []};
    case ActionTypes.ISSUE_CHAT_AMENDMENTS_FETCH.SUCCESS:
      return state === UNINITIALISED
        ? state
        : {
            ...state,
            chatAmendmentsByIssue: {
              ...(state.chatAmendmentsByIssue ?? {}),
              [action.payload.issueId]: action.payload.groups ?? [],
            },
          };
    default:
      return state;
  }
}
